// For stacked bars with mixed positive/negative values, a pure total value cannot
// reliably anchor label position. We use a tiny signed anchor to keep the label
// on the expected side (top for positive total, bottom for negative total).
import { mergeChartDataLabelStyle } from './useChartOptions';

const LABEL_ANCHOR_EPSILON = 0.000001;

interface IAppendStackedTotalLabelOptions {
  enabled: boolean;
  seriesName?: string;
  targetSeriesNames?: string[];
  labelFormatter?: (value: number) => string;
  stackName?: string;
}

export const appendStackedTotalLabelSeries = (
  opt: any,
  {
    enabled,
    seriesName = '__total_label__',
    targetSeriesNames = [],
    labelFormatter = (value: number) => `${value}`,
    stackName,
  }: IAppendStackedTotalLabelOptions,
) => {
  if (!Array.isArray(opt?.series)) {
    return opt;
  }

  const baseSeries = opt.series.filter((series: any) => series?.name !== seriesName);
  opt.series = baseSeries;

  if (!enabled) {
    return opt;
  }

  const legendSelected = (Array.isArray(opt?.legend)
    ? opt?.legend?.[0]?.selected
    : opt?.legend?.selected) || {};
  const targetSeries = baseSeries.filter((series: any) => {
    if (series?.type !== 'bar') {
      return false;
    }
    // Ignore transparent helper series created by prior total-label passes.
    if (
      String(series?.name || '').startsWith('__')
      && String(series?.name || '').endsWith('_total_label__')
    ) {
      return false;
    }
    if (stackName && series?.stack !== stackName) {
      return false;
    }
    // Respect runtime legend hide/show state from chart options.
    // ECharts marks hidden legends as false in legend.selected.
    if (legendSelected?.[series?.name] === false) {
      return false;
    }
    if (!targetSeriesNames.length) {
      return true;
    }
    return targetSeriesNames.some((name) => series?.name?.includes(name));
  });

  if (!targetSeries.length) {
    return opt;
  }

  const maxDataLength = targetSeries.reduce((maxLen: number, series: any) => {
    const dataLength = Array.isArray(series?.data) ? series.data.length : 0;
    return Math.max(maxLen, dataLength);
  }, 0);

  const totalLabelData = Array.from({ length: maxDataLength }).map((_, index) => {
    // We must separate positive and negative stacks; summing everything first and
    // anchoring at total may place labels incorrectly when the stack crosses zero.
    let positiveSum = 0;
    let negativeSum = 0;
    let hasValidValue = false;

    targetSeries.forEach((series: any) => {
      const rawValue = series?.data?.[index];
      const value = rawValue?.value ?? rawValue;
      if (value === null || value === undefined || value === '') {
        return;
      }
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) {
        return;
      }
      hasValidValue = true;
      if (numericValue > 0) {
        positiveSum += numericValue;
      } else if (numericValue < 0) {
        negativeSum += numericValue;
      }
    });

    if (!hasValidValue) {
      return {
        value: null,
        label: {
          show: false,
        },
      };
    }

    const totalValue = positiveSum + negativeSum;
    const labelPosition = totalValue >= 0 ? 'top' : 'bottom';
    // ECharts stacked bar labels can overlap with baseline when value is exactly
    // the total. A tiny epsilon keeps the helper bar in the correct direction
    // without visually changing chart values.
    const anchorValue = totalValue === 0
      ? 0
      : totalValue > 0
        ? LABEL_ANCHOR_EPSILON
        : -LABEL_ANCHOR_EPSILON;

    return {
      value: anchorValue,
      label: mergeChartDataLabelStyle({
        show: true,
        position: labelPosition,
        opacity: 1,
        formatter: () => {
          if (!totalValue || totalValue === 0) {
            return '';
          }
          return labelFormatter(totalValue);
        },
      }),
    };
  });

  const totalLabelSeries = {
    name: seriesName,
    type: 'bar',
    stack: stackName || targetSeries.find((series: any) => series?.stack)?.stack,
    itemStyle: { color: 'transparent' },
    data: totalLabelData,
    z: 10,
    silent: true,
    tooltip: {
      show: false,
    },
    animation: false,
    animationDuration: 0,
    progressive: 0,
    progressiveThreshold: 0,
    animationDurationUpdate: 0,
  };

  opt.series = [...baseSeries, totalLabelSeries];

  if (opt.tooltip?.formatter) {
    const originalFormatter = opt.tooltip.formatter;
    if (
      typeof originalFormatter === 'function'
      && !(originalFormatter as any).__filteredTotalLabel
    ) {
      const wrappedFormatter = (params: any) => {
        const filteredParams = Array.isArray(params)
          ? params.filter((item: any) => item.seriesName !== seriesName)
          : params;
        return originalFormatter(filteredParams);
      };
      (wrappedFormatter as any).__filteredTotalLabel = true;
      opt.tooltip.formatter = wrappedFormatter;
    }
  }

  return opt;
};
